import React from 'react';
import PropTypes from 'prop-types';
import { Fetch } from '../fetcher/fetch';

export const AddressAutoCompleteContext = React.createContext(null);

const endpointApiGouv = 'https://api-adresse.data.gouv.fr/search/';
const defaultParams = {
    limit: 8,
    type: 'housenumber',
    autocomplete: 1
};

export function AddressAutoCompleteContextProvider({ api, children }) {
    const [loadingApi, setLoadingApi] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [fetcher, setFetcher] = React.useState(null);

    window.onGMapsLoaded = () => {
        setLoadingApi(false);
    };

    function loadGoogle() {
        let script_dom = document.querySelector('script.gmaps-neo');
        if (!window.weezConfig.googleAutocomplete) {
            setLoadingApi(false);
            setError(true);
            console.warn('No google_autocomplete key in config');
            return;
        }

        if (!loadingApi || script_dom) {
            // The script is already here & loaded
            setLoadingApi(false);
            return;
        }

        let script = document.createElement('script');
        script.setAttribute('defer', true);
        script.setAttribute('async', true);
        script.classList.add('gmaps-neo');
        script.src = `https://maps.googleapis.com/maps/api/js?key=` + window.weezConfig.googleAutocomplete + `&libraries=places&callback=onGMapsLoaded&loading=async`;
        document.head.appendChild(script);
        script.onerror = (e) => console.warn(e);
    }

    function loadGouv() {
        const fetcher_test = new Fetch(endpointApiGouv);
        const endpoint = endpointApiGouv;
        let query = { ...defaultParams, q: 'health-check' };
		fetcher_test?.request(endpoint, { endpoint, query }).then(() => {
            setFetcher(fetcher_test);
        }).catch((e) => {
            setError(true);
            console.error(e);
        }).finally(() => {
            setLoadingApi(false);
        });
    }

    React.useEffect(() => {
        if (api === 'google') loadGoogle();
        else loadGouv();
    }, [api]);

    return (
        <AddressAutoCompleteContext.Provider
            value={{
                google: window.google,
                loadingApi,
                error,
                fetcher,
                api
            }}
        >
            {children}
        </AddressAutoCompleteContext.Provider>
    );
}

AddressAutoCompleteContextProvider.propTypes = {
    api: PropTypes.string,
    children: PropTypes.node.isRequired
};

AddressAutoCompleteContextProvider.defaultProps = {
    api: 'default',
};

export default function useAddressAutoCompleteContext({ onPlaceDetails = () => {} }) {
    const context = React.useContext(AddressAutoCompleteContext);
    const { google, loadingApi, error, fetcher, api } = context;
    const [addresses, setAddresses] = React.useState([]);
    const [place, setPlace] = React.useState(null);
    const [token, setToken] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const searchGouv = React.useCallback(input => {
        if (input?.trim().length < 3) return setLoading(false);
        const endpoint = endpointApiGouv;
        let query = { ...defaultParams, q: input };
		fetcher?.request(endpoint, { endpoint, query }).then(result => {
			if (result.response && result.response.features.length) {
				let _filters = {};
				if (window.weezConfig && window.weezConfig.address_autocomplete) {
					_filters = {...window.weezConfig.address_autocomplete.filters};
				}
				if (_filters) {
					for (let [filter, filterValue] of Object.entries(_filters)) {
						result.response.features = result.response.features.filter(address => address.properties[filter] === filterValue);
					}
				}
			}
			if (input && !result.response.features.length && input.length > 5) {
				setAddresses([]);
				return;
			}
			if (result.response.features.length === 1 && result.response.features[0].properties.label === input) {
				setAddresses([]);
				return;
			}
            const serializedAddresses = result.response.features.map(f => { return {
                label: f.properties.label,
                address: f.properties.name,
                city: f.properties.city,
                country: 'FR',
                postcode: f.properties.postcode,
                id: f.properties.id,
                coordinates: f.geometry.coordinates.reverse()
            }});
			return setAddresses([...serializedAddresses]);
		}).finally(() => {
			setLoading(false);
		});
    }, [fetcher])

    const searchGoogle = React.useCallback(searchQuery => {
        if (error || loadingApi) {
            return setLoading(false);
        }
        let newToken = token;
        if (!newToken) {
            newToken = new google.maps.places.AutocompleteSessionToken();
        }
        new google.maps.places.AutocompleteService().getPlacePredictions(
            {
                input: searchQuery,
                sessionToken: newToken
            },
            predictions => {
                const serializedAddresses = predictions?.map(f => { return {
                    label: f.description,
                    address: f.structured_formatting.main_text,
                    id: f.place_id,
                }});
                setLoading(false);
                setAddresses(serializedAddresses);
                setToken(newToken);
            }
        );
    }, [google, loadingApi, error, token]);

    const search = React.useCallback(input => {
		setLoading(true);
        if (api === 'google') {
            searchGoogle(input);
        } else {
            searchGouv(input);
        }
    }, [api, searchGoogle, searchGouv]);

    const get_details_google = React.useCallback(place_id => {
        if (error || loadingApi) return;
        const address = addresses.find(a => a.id === place_id);
        let service = new google.maps.places.PlacesService(document.querySelector('div#attribution'));
        service.getDetails(
            {
                placeId: place_id,
                sessionToken: token
            },
            details => {
                let my_types = {};
                details.address_components.forEach(({ long_name, short_name, types }) => {
                    types.forEach(type => {
                        if (type === 'political') {
                            return;
                        }
                        my_types[type] = type !== 'country' ? long_name : short_name;
                    });
                });
                let tmpPlace = {
                    address: {
                        address1: ((my_types?.street_number || '') + ' ' + (my_types?.route || '')).trim(),
                        address2: '',
                        city: my_types?.locality || '',
                        country: my_types?.country || '',
                        state: my_types?.administrative_area_level_1 || '',
                        zip_code: my_types?.postal_code || '',
                        place_id: place_id,
                        name: details.name || '',
                        coordinates: { lat: details.geometry.location.lat(), long: details.geometry.location.lng() }
                    },
                    full_address: details?.formatted_address,
                    raw: address?.label
                };
                setPlace(tmpPlace);
                setToken(new google.maps.places.AutocompleteSessionToken());
                if (onPlaceDetails)
                    onPlaceDetails(tmpPlace);
            }
        )
        },
        [google, addresses, error, loadingApi, token, onPlaceDetails]
    );

    const get_details_gouv = React.useCallback(place_id => {
        const address = addresses.find(a => a.id === place_id);
        if (!address) return;
        const tmpPlace = {
            address: {
                address1: address.address,
                address2: '',
                city: address.city,
                country: address.country,
                state: '',
                zip_code: address.postcode,
                place_id: address.id,
                name: '',
                coordinates: address.coordinates
            },
            full_address: address.label,
            raw: address.label
        };
        setPlace(tmpPlace);
        if (onPlaceDetails)
            onPlaceDetails(tmpPlace);
    }, [onPlaceDetails, addresses])

    const get_details = React.useCallback(place_id => {
        if (api === 'google') get_details_google(place_id);
        else get_details_gouv(place_id);
    }, [api, get_details_google, get_details_gouv]);

    const available = React.useMemo(() => api === 'google' ? !loadingApi && !error && Boolean(google) : !loadingApi && !error && Boolean(fetcher), [api, loadingApi, error, google, fetcher]);
    
    return {search, get_details, addresses, place, loading, loadingApi, error, available};
}
